import { Icon, LinkButton, Text } from "@ttc3k/trekker";
import { AssetFile, AssetImage } from "gql/generated";
import { Lock, Upload } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { CloudinaryImage } from "shared/components/atoms";
import { css } from "styled-system/css";
import { Box, Flex } from "styled-system/jsx";
import { useUploadAvatar } from "./useUploadAvatar";
import { UploadMediaModal } from "shared/components/organisms";
import { limitFill } from "@cloudinary/url-gen/actions/resize";
import { autoGravity } from "@cloudinary/transformation-builder-sdk/qualifiers/gravity";

type UploadAvatarProps = {
  image?: Partial<AssetImage> | undefined;
  isAvatarLocked?: boolean;
  onUploadComplete: (assets: Partial<AssetFile | AssetImage>) => void;
  onRemoveClick: () => void;
  AvatarTag?: React.ReactNode;
  viewOnly?: boolean;
};

export const UploadAvatar = ({
  image,
  isAvatarLocked = false,
  onUploadComplete,
  onRemoveClick,
  AvatarTag,
  viewOnly = false,
}: UploadAvatarProps) => {
  const { t } = useTranslation("core", {
    keyPrefix: "STAFF.ADD_EDIT_PERSON.AVATAR",
  });

  const {
    avatar,
    isUploadModalOpen,
    setIsUploadModalOpen,
    onUploadClick,
    onUploadComplete: onUploadCompleteInternal,
    onRemoveAvatar,
  } = useUploadAvatar({ image, onUploadComplete, onRemoveClick });

  return (
    <>
      <Flex
        flexDir="column"
        alignItems="center"
        justifyContent="center"
        gap="250"
        w="full"
        position="relative"
      >
        {avatar ? (
          <CloudinaryImage
            image={avatar as Partial<AssetImage>}
            imageStyleProps={{
              className: css({
                aspectRatio: "square",
                bgColor: "white",
                borderRadius: "full",
                objectFit: "contain",
                w: "120px",
                h: "120px",
                minW: "120px",
                minH: "120px",
                maxW: "120px",
                maxH: "120px",
              }),
            }}
            resize={limitFill()
              .width(120)
              .gravity(autoGravity())
              .aspectRatio("1:1")}
          />
        ) : (
          <Flex
            alignItems="center"
            justifyContent="center"
            width="120px"
            height="120px"
            bgColor="bg.wa.lightest"
            border="1px dashed"
            borderColor="border.light"
            borderRadius="full"
            cursor={!viewOnly ? "pointer" : "default"}
            tabIndex={0}
            onClick={!viewOnly ? onUploadClick : undefined}
          >
            <Icon Element={Upload} size="lg" color="icon.light" />
          </Flex>
        )}

        {AvatarTag && (
          <Box
            position="absolute"
            top="-11px"
            left="50%"
            transform="translateX(-50%)"
          >
            {AvatarTag}
          </Box>
        )}

        {!viewOnly &&
          (isAvatarLocked ? (
            <Flex alignItems="center" gap="50">
              <Icon Element={Lock} color="icon.light" size="sm" />
              <Text visual="smallMedium" color="text.light">
                {t("LOCKED")}
              </Text>
            </Flex>
          ) : avatar ? (
            <Flex alignItems="center" gap="100">
              <LinkButton
                color="text.accent.blue.mid"
                size="sm"
                type="button"
                onClick={onUploadClick}
              >
                {t("REPLACE")}
              </LinkButton>
              <Box
                borderLeft="1px solid"
                borderColor="border.light"
                height="16px"
              />
              <LinkButton
                color="text.light"
                size="sm"
                type="button"
                onClick={onRemoveAvatar}
              >
                {t("REMOVE")}
              </LinkButton>
            </Flex>
          ) : (
            <LinkButton type="button" onClick={onUploadClick}>
              {t("UPLOAD")}
            </LinkButton>
          ))}
      </Flex>
      <UploadMediaModal
        isOpen={isUploadModalOpen}
        isPublicWidget={false}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={onUploadCompleteInternal}
        maxFiles={1}
      />
    </>
  );
};
